import '@fontsource/dancing-script/400.css';

/**
 * A typed name, drawn in a script face onto a transparent canvas and exported
 * as PNG - the same kind of image a drawn signature is, so the server, the
 * report PDF and the saved signature handle all three alike.
 *
 * The face is bundled with the app (@fontsource, OFL) rather than loaded from
 * Google, and is waited for before drawing: a canvas drawn before the font has
 * arrived silently falls back to the default face.
 */

export const SIGNATURE_FONT = '"Dancing Script", "Segoe Script", cursive';
export const INK = '#0b1f44';

const FONT_SIZE = 96;
const PADDING = 24;
const MAX_WIDTH = 1600;

export const loadSignatureFont = async () => {
  try {
    await document.fonts?.load?.(`${FONT_SIZE}px "Dancing Script"`);
  } catch {
    // Drawn in the fallback face; still a valid signature image.
  }
};

/** @returns {Promise<string|null>} a PNG data URL, or null for an empty name */
export const renderTypedSignature = async (name) => {
  const text = String(name || '').trim();
  if (!text) return null;
  await loadSignatureFont();

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  let size = FONT_SIZE;
  ctx.font = `${size}px ${SIGNATURE_FONT}`;
  let width = ctx.measureText(text).width;
  // A long name shrinks to fit rather than being cut off.
  if (width + PADDING * 2 > MAX_WIDTH) {
    size = Math.floor(size * (MAX_WIDTH - PADDING * 2) / width);
    ctx.font = `${size}px ${SIGNATURE_FONT}`;
    width = ctx.measureText(text).width;
  }

  canvas.width = Math.ceil(width + PADDING * 2);
  canvas.height = Math.ceil(size * 1.6 + PADDING);
  // Resizing a canvas resets its state.
  ctx.font = `${size}px ${SIGNATURE_FONT}`;
  ctx.fillStyle = INK;
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(text, PADDING, Math.round(size * 1.1 + PADDING / 2));
  return canvas.toDataURL('image/png');
};

export default renderTypedSignature;
